import type { Editor } from '@tiptap/react';

import type { IEditorQuery } from './../ricos-content-query';
import type { ModalService } from './../ricos-modals';
import type { IToolbarItem, PublicCommands, RicosServices } from './../ricos-types';

import { Content } from './Content';
import EventEmitter from './lib/EventEmitter';
import type { IToolbarItemConfig } from './types';

export class ToolbarItemCreator {
  static create(toolbarItemConfig: IToolbarItemConfig) {
    return (
      content: RicosServices['content'],
      tiptapEditor: Editor,
      editorQuery: IEditorQuery,
      modalService: ModalService,
      publicCommands: PublicCommands
    ) => {
      return new ToolbarItem(
        toolbarItemConfig,
        content,
        tiptapEditor,
        editorQuery,
        modalService,
        publicCommands
      );
    };
  }
}

export class ToolbarItem extends EventEmitter implements IToolbarItem {
  static EVENTS = {
    ATTRIBUTES_CHANGED: 'ATTRIBUTES_CHANGED',
    COMMAND_EXECUTED: 'COMMAND_EXECUTED',
  } as const;

  private toolbarItem: IToolbarItem;

  constructor(
    private toolbarItemConfig: IToolbarItemConfig,
    private content: RicosServices['content'],
    private tiptapEditor: Editor,
    private editorQuery: IEditorQuery,
    private modalService: ModalService,
    private publicCommands: PublicCommands
  ) {
    super();
    this.toolbarItemConfig = toolbarItemConfig;
    this.toolbarItem = {
      id: toolbarItemConfig.id,
      presentation: toolbarItemConfig.presentation,
      type: toolbarItemConfig.type,
      attributes: {},
      commands: {},
    };

    this.content.on(Content.EVENTS.contentChangeEvent, () => {
      this.updateAttributes();
    });

    modalService.onModalOpened(() => {
      this.updateAttributes();
    });

    modalService.onModalClosed(() => {
      this.updateAttributes();
    });
  }

  setup() {
    this.toolbarItem.attributes = this.getAttributes();
    this.assignCommands();
  }

  destroy() {
    this.removeAllListeners();
  }

  assignCommands() {
    const getAttributes = this.getAttributes.bind(this);

    Object.keys(this.toolbarItemConfig.commands).forEach(commandName => {
      this.toolbarItem.commands[commandName] = (...args: [args: any]) => {
        const command = this.toolbarItemConfig.commands[commandName]({
          attributes: getAttributes(),
          tiptapEditor: this.tiptapEditor,
          publicCommands: this.publicCommands,
          editorQuery: this.editorQuery,
        });

        // eslint-disable-next-line prefer-spread
        const returnValue = command.apply(null, args);

        this.emit(ToolbarItem.EVENTS.COMMAND_EXECUTED, {
          commandName,
          args,
        });

        return returnValue;
      };
    });
  }

  public updateAttributes() {
    this.toolbarItem.attributes = this.getAttributes();
  }

  private getAttributes() {
    const attributeResolvers = this.toolbarItemConfig.attributes;

    return Object.keys(attributeResolvers)
      .map(attributeName => {
        return {
          attributeName,
          attribute: this.content.resolve(attributeResolvers[attributeName]),
        };
      })
      .reduce((acc, { attributeName, attribute }) => {
        return {
          ...acc,
          [attributeName]: attribute,
        };
      }, {});
  }

  get id() {
    return this.toolbarItem.id;
  }

  get presentation() {
    return this.toolbarItem.presentation;
  }

  get attributes() {
    return this.toolbarItem.attributes;
  }

  get commands() {
    return this.toolbarItem.commands;
  }

  get type() {
    return this.toolbarItem.type;
  }
}
